import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Modal, Form } from 'react-bootstrap'
import { toast } from '@CommonComponents/toast'
import { client, MAX_LATE_DEDUCTIONS } from '@Utils'
import { useStateValue } from '@State'
import { CommaListEditor } from '../CommaListEditor'

export const EditDeploymentModal = ({ show, toggle, deployment }) => {
  const [currentState, dispatch] = useStateValue()
  const [stateObj, setStateObj] = useState(deployment)

  // UI state for the rows in the deductions table
  const [lateDeductions, setLateDeductions] = useState([])

  // --- helpers for parsing/serializing late deductions ---

  const parseLateDeductions = raw => {
    if (!raw) return []

    var arr = raw

    // If it's a string like "[[1,'d',100.0,'percent']]"
    if (typeof arr === 'string') {
      try {
        // Convert single quotes to double quotes for JSON.parse
        var jsonStr = arr.replace(/'/g, '"')
        arr = JSON.parse(jsonStr)
      } catch (e) {
        console.warn('Could not parse defaultLatePolicyDeductions:', raw, e)
        return []
      }
    }

    if (!Array.isArray(arr)) return []

    return arr.map(function (inner) {
      inner = Array.isArray(inner) ? inner : []

      return {
        offset:
          inner.length > 0 && inner[0] !== null && inner[0] !== undefined
            ? inner[0]
            : '',
        unit:
          inner.length > 1 && inner[1] !== null && inner[1] !== undefined
            ? inner[1]
            : 'd',
        amount:
          inner.length > 2 && inner[2] !== null && inner[2] !== undefined
            ? inner[2]
            : '',
        mode:
          inner.length > 3 && inner[3] !== null && inner[3] !== undefined
            ? inner[3]
            : 'percent',
      }
    })
  }

  const serializeLateDeductions = rows => {
    if (!rows || !rows.length) return ''

    // Only include rows that have both an offset and amount
    const inner = rows
      .filter(r => r.offset !== '' && r.amount !== '')
      .map(r => {
        const offset = Number(r.offset) || 0
        const amount = Number(r.amount) || 0
        const unit = r.unit || 'd'
        const mode = r.mode || 'percent'
        return `[${offset},'${unit}',${amount},'${mode}']`
      })

    if (!inner.length) return ''
    return `[${inner.join(',')}]`
  }

  const syncLateDeductionsToStateObj = updatedRows => {
    const serialized = serializeLateDeductions(updatedRows)

    setLateDeductions(updatedRows)

    setStateObj(prev => {
      const prevData = (prev && prev.data) || {}
      const prevSnap = prevData.snap || {}

      return {
        ...prev,
        data: {
          ...prevData,
          snap: {
            ...prevSnap,
            defaultLatePolicyDeductions: serialized,
          },
        },
      }
    })
  }

  const handleLateDeductionChange = (index, field, value) => {
    const updated = lateDeductions.map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    )
    syncLateDeductionsToStateObj(updated)
  }

  const addLateDeductionRow = () => {
    if (lateDeductions.length >= MAX_LATE_DEDUCTIONS) return
    const updated = [
      ...lateDeductions,
      { offset: '1', unit: 'd', amount: '100.0', mode: 'percent' },
    ]
    syncLateDeductionsToStateObj(updated)
  }

  const removeLateDeductionRow = index => {
    const updated = lateDeductions.filter((_, i) => i !== index)
    syncLateDeductionsToStateObj(updated)
  }

  useEffect(() => {
    if (!deployment) return

    const baseData = deployment.data || {}
    const baseSnap = baseData.snap || {
      institutionCourses: '',
      usesInstitutionCourses: false,
      shouldConnectToSourceCourse: false,
      institutionManagers: '',
      scoreDecisionHandler: '',
      dueDateHandler: '',
      submissionLimitHandler: '',
      defaultLatePolicy: '',
      defaultLatePolicyDeductions: '',
    }

    const newStateObj = {
      ...deployment,
      data: {
        ...baseData,
        snap: baseSnap,
      },
    }

    setStateObj(newStateObj)

    const parsed = parseLateDeductions(
      baseSnap.defaultLatePolicyDeductions || ''
    )

    setLateDeductions(
      parsed.length
        ? parsed
        : [{ offset: '', unit: 'd', amount: '', mode: 'percent' }]
    )
  }, [deployment])

  const commitStateObject = async () => {
    try {
      // switch data for mydata for update; See Item structure in dserv project
      const fullStateObj = { ...stateObj, mydata: stateObj.data }

      const resp = await client.post(
        `/account/admin_lti13.editDeployment/-/${currentState.issuer.id}/${currentState.client.id}/${deployment.id}`,
        fullStateObj
      )
      toast.success('Successfully edited the deployment.')
      dispatch({ type: 'updateDeployment', deployment: resp.data.deployment })
    } catch (err) {
      console.log(err)
      let errMsg = 'There was an error trying to update the Platform Instance.'
      if (err.response) {
        errMsg = `Error: ${err.response.data.error}`
      }
      toast.error(errMsg)
    } finally {
      toggle(!show)
    }
  }

  const handleChange = e => {
    const { name, type, checked, value } = e.target

    setStateObj(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleDataChange = e => {
    const { name, type, checked, value } = e.target

    setStateObj(prev => {
      const prevData = prev.data || {}
      return {
        ...prev,
        data: {
          ...prevData,
          [name]: type === 'checkbox' ? checked : value,
        },
      }
    })
  }

  const setSnapValue = (name, value) => {
    setStateObj(prev => {
      const prevData = prev.data || {}
      const prevSnap = prevData.snap || {}
      return {
        ...prev,
        data: {
          ...prevData,
          snap: {
            ...prevSnap,
            [name]: value,
          },
        },
      }
    })
  }

  const handleDataSnapChange = e => {
    const { name, type, checked, value } = e.target
    setSnapValue(name, type === 'checkbox' ? checked : value)
  }

  const helpTextStyle = {
    fontSize: '0.8em',
    color: '#6B5400',
  }

  return (
    <Modal show={show} onHide={() => toggle(!show)}>
      <Modal.Header closeButton>
        <Modal.Title>Edit Deployment Instance</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group controlId="name">
          <Form.Label>Name:</Form.Label>
          <Form.Control
            type="text"
            onChange={handleDataChange}
            name="name"
            value={
              stateObj && stateObj.data && stateObj.data.name
                ? stateObj.data.name
                : ''
            }
          />
        </Form.Group>
        <Form.Group controlId="deploymentid">
          <Form.Label>Deployment ID:</Form.Label>
          <Form.Control
            type="text"
            onChange={handleChange}
            name="deploymentid"
            disabled={true}
            value={
              stateObj && stateObj.deploymentid ? stateObj.deploymentid : ''
            }
          />
        </Form.Group>
        <Form.Group controlId="useSourceCourse">
          <Form.Label>Use Source Course on Init:</Form.Label>
          <Form.Check
            className="ml-2 mb-4 form-inline-b3"
            onChange={handleDataSnapChange}
            name="shouldConnectToSourceCourse"
            checked={
              stateObj &&
              stateObj.data &&
              stateObj.data.snap &&
              stateObj.data.snap.shouldConnectToSourceCourse
                ? true
                : false
            }
          />
          <div style={helpTextStyle}>
            If checked, will connect the selected course instead of duplicate a
            new one from the selection
          </div>
        </Form.Group>
        <Form.Group controlId="institutionCourses">
          <Form.Label>Use Institution Courses:</Form.Label>
          <Form.Check
            className="ml-2 mb-4 form-inline-b3"
            onChange={handleDataSnapChange}
            name="usesInstitutionCourses"
            checked={
              stateObj &&
              stateObj.data &&
              stateObj.data.snap &&
              stateObj.data.snap.usesInstitutionCourses
                ? true
                : false
            }
          />
          <div style={helpTextStyle}>
            If checked, use the list defined below instead of the users course
            memberships to populate the course list
          </div>
        </Form.Group>

        <Form.Group controlId="institutionCourses">
          <Form.Label>Institution Courses:</Form.Label>
          <CommaListEditor
            value={
              stateObj &&
              stateObj.data &&
              stateObj.data.snap &&
              stateObj.data.snap.institutionCourses
                ? stateObj.data.snap.institutionCourses
                : ''
            }
            onChange={val => setSnapValue('institutionCourses', val)}
            placeholder="Coursenum (e.g. 1234)"
            addLabel="Add Course"
          />
          <div style={helpTextStyle}>
            Coursenums to use in conjunction with the above option. Press Enter
            or click Add Course; paste a comma-separated list to add several at
            once.
          </div>
        </Form.Group>

        <Form.Group controlId="institutionManagers">
          <Form.Label>Institution Managers:</Form.Label>
          <CommaListEditor
            value={
              stateObj &&
              stateObj.data &&
              stateObj.data.snap &&
              stateObj.data.snap.institutionManagers
                ? stateObj.data.snap.institutionManagers
                : ''
            }
            onChange={val => setSnapValue('institutionManagers', val)}
            placeholder="Email address"
            addLabel="Add Manager"
          />
          <div style={helpTextStyle}>
            Users with these emails will be given the manager role during an
            LTI 1.3 launch. Press Enter or click Add Manager; paste a
            comma-separated list to add several at once.
          </div>
        </Form.Group>

        <Form.Group controlId="defaultLatePolicy">
          <Form.Label>Default Late Policy:</Form.Label>
          <Form.Control
            as="select"
            onChange={handleDataSnapChange}
            name="defaultLatePolicy"
            value={
              stateObj &&
              stateObj.data &&
              stateObj.data.snap &&
              typeof stateObj.data.snap.defaultLatePolicy === 'string'
                ? stateObj.data.snap.defaultLatePolicy
                : ''
            }
          >
            <option value="">No Policy</option>
            <option value="flag">Flag</option>
            <option value="prevent">Prevent</option>
            <option value="deduct">Deduct</option>
          </Form.Control>
          <div style={helpTextStyle}>
            Flag as late only, prevent submissions, or deduct points according
            to deductions
          </div>
        </Form.Group>

        <Form.Group controlId="defaultLatePolicyDeductions">
          <Form.Label>Default Late Policy Deductions:</Form.Label>

          {/* Column headers */}
          <div
            className="d-flex mb-1"
            style={{ fontSize: '0.8em', fontWeight: 'bold' }}
          >
            <div style={{ flex: 1 }}>After</div>
            <div style={{ width: '30%' }}>Unit</div>
            <div style={{ flex: 1 }}>Amount</div>
            <div style={{ width: '110px' }}>Type</div>
            <div style={{ width: '40px' }} />
          </div>

          {/* Rows */}
          {lateDeductions.map((row, index) => (
            <div className="d-flex mb-2" key={index}>
              {/* Offset (int) */}
              <Form.Control
                type="number"
                min="0"
                step="1"
                placeholder="1"
                value={row.offset}
                onChange={e =>
                  handleLateDeductionChange(index, 'offset', e.target.value)
                }
                style={{ flex: 1, marginRight: '4px' }}
              />

              {/* Unit: d/h/m */}
              <Form.Control
                as="select"
                value={row.unit}
                onChange={e =>
                  handleLateDeductionChange(index, 'unit', e.target.value)
                }
                style={{ width: '30%', marginRight: '4px' }}
              >
                <option value="d">days (d)</option>
                <option value="h">hours (h)</option>
                <option value="m">minutes (m)</option>
              </Form.Control>

              {/* Amount (decimal) */}
              <Form.Control
                type="number"
                step="0.01"
                placeholder="100"
                value={row.amount}
                onChange={e =>
                  handleLateDeductionChange(index, 'amount', e.target.value)
                }
                style={{ flex: 1, marginRight: '4px' }}
              />

              {/* Mode: points/percent */}
              <Form.Control
                as="select"
                value={row.mode}
                onChange={e =>
                  handleLateDeductionChange(index, 'mode', e.target.value)
                }
                style={{ width: '110px', marginRight: '4px' }}
              >
                <option value="points">points</option>
                <option value="percent">percent</option>
              </Form.Control>

              {/* Remove row */}
              <button
                type="button"
                className="btn btn-link btn-sm text-danger"
                onClick={() => removeLateDeductionRow(index)}
                title="Remove this deduction"
              >
                &times;
              </button>
            </div>
          ))}

          {/* Add row button */}
          {lateDeductions.length < MAX_LATE_DEDUCTIONS && (
            <button
              type="button"
              className="btn btn-link btn-sm p-0 mb-2"
              onClick={addLateDeductionRow}
            >
              + Add deduction
            </button>
          )}

          <div style={helpTextStyle}>
            Nested array structure of late penalties to be applied as a default.
            Only used if Default Late Policy is set to Deduct. Each row becomes
            an array like [int, &apos;d/h/m&apos;, decimal,
            &apos;points/percent&apos;]. Example: after 1 day, deduct 100
            percent → [1, &apos;d&apos;, 100.0, &apos;percent&apos;].
          </div>
        </Form.Group>

        <Form.Group controlId="dueDateHandler">
          <Form.Label>Due Date Handler:</Form.Label>
          <Form.Control
            as="select"
            onChange={handleDataSnapChange}
            name="dueDateHandler"
            value={
              stateObj &&
              stateObj.data &&
              stateObj.data.snap &&
              typeof stateObj.data.snap.dueDateHandler === 'string'
                ? stateObj.data.snap.dueDateHandler
                : ''
            }
          >
            <option value="">No handler</option>
            <option value="base">Base</option>
            <option value="ags">AGS Lookup</option>
            <option value="canvas">Canvas</option>
            <option value="brightspace">Brightspace</option>
          </Form.Control>

          <div style={helpTextStyle}>
            The handler for how to get the due date for a launched assessment.
            Base handler currently does not do anything. AGS uses the AGS API to
            get the endDateTime from the LMS. Canvas extends AGS and also
            supports student-specific due dates via custom data. Brightspace
            reads the due date from custom data, since Brightspace does not
            send it through AGS.
          </div>
        </Form.Group>

        <Form.Group controlId="scoreDecisionHandler">
          <Form.Label>Score Decision Handler:</Form.Label>
          <Form.Control
            as="select"
            onChange={handleDataSnapChange}
            name="scoreDecisionHandler"
            value={
              stateObj &&
              stateObj.data &&
              stateObj.data.snap &&
              typeof stateObj.data.snap.scoreDecisionHandler === 'string'
                ? stateObj.data.snap.scoreDecisionHandler
                : ''
            }
          >
            <option value="">No handler</option>
            <option value="base">Normal</option>
            <option value="4_days_late">4 day limit</option>
          </Form.Control>

          <div style={helpTextStyle}>
            Select which submission handler to apply for this deployment. This
            determines if a score should be sent to the LMS or not
          </div>
        </Form.Group>

        <Form.Group controlId="submissionLimitHandler">
          <Form.Label>Submission Limit Handler:</Form.Label>
          <Form.Control
            as="select"
            onChange={handleDataSnapChange}
            name="submissionLimitHandler"
            value={
              stateObj &&
              stateObj.data &&
              stateObj.data.snap &&
              typeof stateObj.data.snap.submissionLimitHandler === 'string'
                ? stateObj.data.snap.submissionLimitHandler
                : ''
            }
          >
            <option value="">No handler</option>
            <option value="base">Base</option>
            <option value="canvas">Canvas</option>
          </Form.Control>

          <div style={helpTextStyle}>
            The handler for how to get the submission limit for a launched
            assessment. Base handler currently does not do anything. Canvas
            reads the allowed attempts from custom data sent during the LTI
            launch.
          </div>
        </Form.Group>

        <div
          className="btn btn-success btn-myed btn-success-myed"
          onClick={() => commitStateObject()}
        >
          <div className="fa-regular fa-plus fa-fw mr-2" /> Update
        </div>
      </Modal.Body>
    </Modal>
  )
}

EditDeploymentModal.propTypes = {
  show: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  deployment: PropTypes.object,
}
